/**
 * @module builder
 */
import type { OptionsDocument, QuoteDocument } from 'paintscout'
import type { UpdateableGroup } from '../index'
import { getFileKeysForItem, getGroupSubstrateChildren, getItem, updateGroup } from '../../index'

import findIndex from 'lodash/findIndex'
import difference from 'lodash/difference'

import { getGroupDetails } from '../get-group-details'

export function removeItemsFromGroup(args: {
  quote: QuoteDocument
  options: OptionsDocument
  group: UpdateableGroup
  keys: string[]
}): QuoteDocument {
  const { quote, options, group, keys: removeKeys } = args
  const { view } = group

  const keys = difference(group.children, removeKeys)

  const substrateChildren = getGroupSubstrateChildren({
    quote,
    options,
    section: group.section,
    keys,
    groupKey: group.key
  })
  let groupSpecificFiles = group?.files ?? []

  removeKeys.forEach((key) => {
    if (view) {
      const orderItemIndex = findIndex(quote.order[view], { key })

      delete quote.order[view][orderItemIndex].parent
    } else {
      // Need to check both and delete from existing, if no view on group
      // This happens when group of line-items, as they are in both
      const areaOrderItemIndex = findIndex(quote.order['area'], { key })
      const substrateOrderItemIndex = findIndex(quote.order['substrate'], { key })
      if (areaOrderItemIndex !== -1) {
        delete quote.order['area'][areaOrderItemIndex].parent
      }
      if (substrateOrderItemIndex !== -1) {
        delete quote.order['substrate'][substrateOrderItemIndex].parent
      }
    }

    const item = getItem({ quote, options, key })
    const itemImages = getFileKeysForItem({ item })
    groupSpecificFiles = difference(groupSpecificFiles, itemImages)
  })

  const {
    description: defaultDescription,
    price,
    files,
    quoteType,
    view: newGroupView
  } = getGroupDetails({
    quote: {
      ...quote,
      groups: {
        ...quote.groups,
        [group.key]: {
          ...group,
          children: keys,
          substrateChildren
        }
      }
    },
    options,
    keys,
    groupKey: group.key
  })
  groupSpecificFiles = difference(groupSpecificFiles, files)

  const updatedGroup = {
    ...group,
    files: [...groupSpecificFiles, ...files],
    children: keys,
    substrateChildren,
    description: {
      ...quote.groups[group.key].description,
      default: defaultDescription
    },
    price: {
      ...quote.groups[group.key].price,
      default: price
    },
    quoteType,
    view: newGroupView
  }

  const updatedQuote = updateGroup({
    quote,
    options,
    group: updatedGroup,
    calculate: true,
    section: group.section
  })

  return updatedQuote
}
